import { useTranslation } from 'react-i18next';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Button, Tabs, TabsContent, TabsList, TabsTrigger, toast } from '@cuspar/ui';
import { useState } from 'react';
import { useCartStore } from '../../state/cartStore';
import { IPurchaseDomainType, ICommonAddItemToCartRequest } from '@cuspar/ywt.ch.model';
import { ROUTES } from '../../routes';
import { DomainTransferForm } from '../../components/Forms/DomainTransferForm';
import { domainAPI } from '../../state/api/domain';
import { BatchTransfer } from '../../components/Domain/BatchTransfer';

type TransferTab = 'single' | 'batch';

export const TransferDomainPage = () => {
  const { t } = useTranslation();
  const [addingToCartDomain, setAddingToCartDomain] = useState(false);
  const [batchBusy, setBatchBusy] = useState(false);
  const [tab, setTab] = useState<TransferTab>('single');
  const navigate = useNavigate();

  const addItemToCart = useCartStore((state) => state.addItemToCart);

  async function addDomainToCart({ domain, code }: { domain: string; code: string }) {
    setAddingToCartDomain(true);
    const response = await domainAPI.checkDomainTransferAvailability(domain, code);

    if (!response.success) {
      toast.warning(response.reason ?? t('domain.transferNotAvailable'));
      setAddingToCartDomain(false);
      return;
    }

    // if it is available, meaning the domain is not registered, show a warning
    if (!response.data.available) {
      toast.warning(response.data.reason ?? t('domain.transferNotAvailable'));
      setAddingToCartDomain(false);
      return;
    }

    const domainCartItem: ICommonAddItemToCartRequest = {
      type: 'domain',
      item: {
        domain: domain,
        type: IPurchaseDomainType.Transfer,
        transferAuthCode: code,
        price: 0,
      },
    };
    const result = await addItemToCart(domainCartItem);
    setAddingToCartDomain(false);
    if (result.success) navigate(ROUTES.checkout.cart);
  }

  return (
    <>
      <h1 className='font-mono text-center text-2xl md:text-4xl font-bold leading-tight tracking-tighter lg:leading-[1.1] mb-2'>{t('domain.transferTitle')}</h1>
      <p className='text-center text-sm text-muted-foreground mb-10'>
        <Trans i18nKey='domain.transferDescription' components={{ bold: <span className='font-bold text-foreground' /> }} />
      </p>
      <Tabs value={tab} onValueChange={(value) => setTab(value as TransferTab)} className='container max-w-5xl mx-auto'>
        <TabsList className='mb-6 flex w-fit mx-auto'>
          <TabsTrigger value='single' disabled={batchBusy}>
            {t('domain.batch.single')}
          </TabsTrigger>
          <TabsTrigger value='batch' disabled={addingToCartDomain}>
            {t('domain.batch.title')}
          </TabsTrigger>
        </TabsList>
        {/* Radix unmounts an inactive panel, which would throw away a pasted and already checked
            batch preview when the customer glances at the other tab. Keep both panels mounted and
            hide the inactive one ourselves (forceMount alone leaves both visible). */}
        <TabsContent value='single' forceMount hidden={tab !== 'single'}>
          <DomainTransferForm className='max-w-3xl mx-auto' onSubmit={addDomainToCart}>
            <Button loading={addingToCartDomain} size='sm'>
              {t('domain.addToCart')}
            </Button>
          </DomainTransferForm>
        </TabsContent>
        <TabsContent value='batch' forceMount hidden={tab !== 'batch'}>
          <BatchTransfer onBusyChange={setBatchBusy} />
        </TabsContent>
      </Tabs>
    </>
  );
};
